<script lang="ts" setup>
	import { ref } from 'vue'
	import Floater from '@/components/common/Floater.vue'
	import MainModal from '@/components/common/MainModal.vue'
	import { TriangleAlert } from 'lucide-vue-next'
	import { useI18n } from 'vue-i18n'
	import { _translations } from './_translations'

	// if the popper version is used:
	// try to min-h-96 on the parent element if using overflow-auto

	interface Props {
		modal?: boolean
		title?: string
		text?: string
		confirmOnEnter?: boolean | string
		placement?: 'top' | 'bottom' | 'left' | 'right'
		compact?: boolean
	}

	const props = withDefaults(defineProps<Props>(), {
		modal: false,
		title: '',
		text: '',
		confirmOnEnter: false,
		placement: 'top',
		compact: false,
	})

	const emits = defineEmits(['confirm'])
	const { t } = useI18n(_translations)

	const isDialogOpen = ref(false)
	const cancelButtonRef = ref<HTMLButtonElement | null>(null)
	const confirmButtonRef = ref<HTMLButtonElement | null>(null)

	function focusActionButton() {
		if (props.confirmOnEnter) confirmButtonRef.value?.focus()
		// else cancelButtonRef.value?.focus()
	}

	function openDialog() {
		isDialogOpen.value = true
		focusActionButton()
	}
	function cancelDialog() {
		isDialogOpen.value = false
	}
	function confirmDialog() {
		isDialogOpen.value = false
		emits('confirm')
	}

	function confirmPopper(close: () => void) {
		close()
		emits('confirm')
	}
</script>

<template>
	<template v-if="modal">
		<button type="button" class="inline-flex" @click="openDialog">
			<slot />
		</button>

		<MainModal
			v-model="isDialogOpen"
			size="sm"
			:show-close="false"
			:close-on-overlay="false"
			:close-on-esc="false"
			@opened="focusActionButton">
			<div class="max-w-md p-4">
				<h3 v-if="title" class="flex items-center gap-2 p-1 text-lg font-bold">
					<TriangleAlert class="text-warning inline h-6 w-6" />
					{{ title }}
				</h3>
				<p v-if="text" class="p-1 whitespace-break-spaces">{{ text }}</p>
				<div class="modal-action">
					<button ref="cancelButtonRef" class="btn" @click="cancelDialog">
						{{ t('buttons.cancel') }}
					</button>
					<button ref="confirmButtonRef" class="btn btn-primary" @click="confirmDialog">
						{{ t('buttons.confirm') }}
					</button>
				</div>
			</div>
		</MainModal>
	</template>

	<!-- Popper Version -->
	<template v-else>
		<Floater offsetDistance="0" :placement="placement">
			<span>
				<slot />
			</span>
			<template #content="{ close }">
				<div class="">
					<h3 v-if="title" class="mb-2 text-lg font-bold">{{ title }}</h3>
					<p v-if="text" :class="compact ? 'mb-2 text-sm' : 'mb-4'">{{ text }}</p>
					<div class="flex justify-end gap-2">
						<button
							:class="
								compact
									? 'btn btn-xs btn-error'
									: 'btn btn-sm btn-wide btn-error w-full min-w-48'
							"
							@click.stop="confirmPopper(close)">
							{{ t('buttons.confirm') }}
						</button>
					</div>
				</div>
			</template>
		</Floater>
	</template>
</template>
